import React, { useState } from 'react';
import { Button, Card, Flex, Space, Typography } from 'antd';
import { UpOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { useTheme } from '../../../../../hooks/useTheme';
import { Size, useWindowSize } from '../../../../../hooks/useWindowSize';
import {
    PaymentSystemName,
    PaymentSystemProps,
} from '../service/paymentSettings.interface';
import { AVAILABLE_SYSTEMS } from '../utils/options';
import { useUpdatePaymentSystemsMutation } from '../service/paymentSettings.api';
import { useApiErrorNotification } from '../../../../../hooks/useApiErrorNotification';
import { useApiSuccessNotification } from '../../../../../hooks/useApiSuccessNotification';

interface Props {
    readonly loading: boolean;
    readonly system: PaymentSystemProps;
    readonly openSystem: string | null;
    readonly setOpenSystem: (name: string | null) => void;
}
export const PaymentSystemCard = ({
    loading,
    system,
    openSystem,
    setOpenSystem,
}: Props) => {
    const token = useTheme();
    const { size } = useWindowSize();
    const { id } = useParams();

    const [savePaymentSystem, { isError, error, data, isSuccess }] =
        useUpdatePaymentSystemsMutation();

    useApiErrorNotification(isError, error);
    useApiSuccessNotification(isSuccess, data);

    const handleToggle = (item: PaymentSystemProps) => {
        setOpenSystem(openSystem === item.name ? null : item.name);
    };

    const updatePaymentSystem = async (name: PaymentSystemName) => {
        const payload = {
            payment_system: name,
            id: +id,
        };
        try {
            await savePaymentSystem(payload).unwrap();
        } catch (error) {
            console.error('Ошибка при обновлении системы:', error);
        }
    };

    const isOpen = openSystem === system.name;
    const systemConfig = AVAILABLE_SYSTEMS.find(s => s.name === system.name);
    const steps = systemConfig?.steps || [];

    return (
        <Card type='inner' loading={loading}>
            <Flex
                vertical
                wrap={'wrap'}
                justify={'space-between'}
                gap={'1.5rem'}
            >
                <Flex
                    gap={16}
                    align={'center'}
                    style={{
                        marginBottom: size === Size.small ? '1.5rem' : 0,
                        cursor: 'pointer',
                    }}
                    onClick={() => handleToggle(system)}
                >
                    {system.active && (
                        <CheckCircleOutlined
                            style={{
                                color: token.colorPrimary,
                                fontSize: 24,
                            }}
                        />
                    )}
                    <StyledTitle>
                        <Typography.Title style={{ margin: 0 }} level={2}>
                            {system.brand}
                        </Typography.Title>
                    </StyledTitle>

                    <UpOutlined
                        rotate={isOpen ? 0 : 180}
                        style={{
                            color: token.colorTextBase,
                        }}
                    />
                </Flex>

                <Flex vertical={false} justify={'space-between'}>
                    {isOpen ? (
                        <Flex vertical gap={16}>
                            {steps.map((StepComponent, index) => (
                                <StepComponent key={index} system={system} />
                            ))}
                        </Flex>
                    ) : (
                        <div />
                    )}

                    {system && (
                        <Flex gap={24} justify={'flex-end'} align={'flex-end'}>
                            <Space
                                align={'center'}
                                direction={'horizontal'}
                                wrap
                            >
                                {!system.connect && !system.active && (
                                    <Button
                                        disabled={loading}
                                        onClick={() => handleToggle(system)}
                                    >
                                        Настроить
                                    </Button>
                                )}
                                {system.connect && !system.active && (
                                    <Button
                                        disabled={loading}
                                        onClick={() =>
                                            updatePaymentSystem(system.name)
                                        }
                                    >
                                        Подключить
                                    </Button>
                                )}
                                {system.connect && system.active && (
                                    <Button
                                        block
                                        disabled={loading}
                                        type='primary'
                                        onClick={() => handleToggle(system)}
                                    >
                                        Подключено
                                    </Button>
                                )}
                            </Space>
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Card>
    );
};

const StyledTitle = styled.div`
    flex: 1 1 0;
`;
